import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { IRole } from '@core/interfaces/role.interface';
import { ITeam } from '@core/interfaces/team.interface';
import { IUser } from '@core/interfaces/user.interface';
import { RoleService } from '@core/services/role.service';
import { TeamService } from '@core/services/team.service';
import { UserInvitationService } from '@core/services/user-invitation.service';
import { UserService } from '@core/services/user.service';
import { ComponentStore } from '@ngrx/component-store';
import { IUserInvitation } from '@shared/interfaces/user-invitation.interface';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

// Every tab/dialog reads the full set with no server-side pagination - 1000 is a pragmatic cap,
// same reasoning the previous per-tab stores used.
const FETCH_LIMIT = '1000';

interface IUserManagementSharedDataState {
  roles: IRole[] | null;
  teams: ITeam[] | null;
  users: IUser[] | null;
  /** Every campaign-sub-user invitation (pending, expired, and accepted alike - unlike
   * UserManagementUsersTabStore's own copy, this one is never filtered down to a single tab's
   * display needs) - used only by UserManagementRolesTeamsTabComponent, to count a role/team's
   * still-open (not yet accepted) invitations toward whether it can be deleted. See
   * ensureUserInvitationsLoaded's doc comment for why this is a separate fetch from
   * UserManagementUsersTabStore's, rather than reusing that store's copy directly. */
  userInvitations: IUserInvitation[] | null;
}

const initialState: IUserManagementSharedDataState = {
  roles: null,
  teams: null,
  userInvitations: null,
  users: null,
};

/**
 * Single source of truth for roles/teams/users shared across the Users tab and the Roles & Teams
 * tab (both mount simultaneously - `user-management.component.html`'s `mat-tab-group` eagerly
 * renders every `<mat-tab>` body, no `*matTabContent` lazy directive is used). Provided on
 * `UserManagementComponent` (see its `providers` array) rather than root: both tabs are direct
 * descendants of it in the component tree and resolve this same instance through ordinary
 * hierarchical DI, and every dialog this feature opens (Invite, Select Role, Select Team, Add
 * Custom Role, Add Team) receives its data via `MAT_DIALOG_DATA` and hands its result back via
 * `dialogRef.close()` rather than injecting this store directly - the caller (always one of the
 * two tabs) does the actual state mutation - so nothing here ever needs to be reachable from a
 * bare `MatDialog.open()` call.
 *
 * Being component-scoped (a former version of this class was a plain `providedIn: 'root'`
 * service) is what makes a *fresh* instance - and a fresh fetch of everything - happen every time
 * the Settings > User Management page is opened: a root singleton kept serving whatever it last
 * fetched, potentially long stale, to every future visit for the rest of the app's lifetime.
 * `UserManagementComponent`'s constructor calls `ensureLoaded()` exactly once per visit; each
 * `ensure*Loaded()` guards against a duplicate fetch with a null check plus a private in-flight
 * flag (both tabs read from this store immediately on mount, but only `UserManagementComponent`
 * triggers the fetch). Every mutation method below keeps this in-memory state in sync so both
 * tabs (and whichever dialog's caller invokes them) update reactively without re-fetching - this
 * is what keeps role/team changes, and role/team/user CRUD, in sync across the whole feature.
 *
 * Fetch failure handling: `fetchRoles`/`fetchTeams`/`fetchUsers`' `catchError` branches
 * deliberately never `patchState` on failure, whether triggered by `ensure*Loaded()` (a genuine
 * initial load) or `reload*()` (resyncing after an optimistic delete's request failed). Writing
 * `[]` on a genuine initial load failure used to masquerade it as "successfully loaded, genuinely
 * empty" - every consumer here (roleRows/teamRows in the Roles & Teams tab, in particular) already
 * correctly treats `null` as "still unavailable" and refuses to compute from it, but silently
 * zeroed out every role/team's member count once the list was `[]` instead (member count is
 * derived by cross-referencing against `users$()`), which let a role/team that actually has
 * members be deleted as if it didn't. Leaving state at `null` also keeps `ensure*Loaded()`'s guard
 * retry-able instead of a failed load permanently looking "already loaded". The global
 * ApiInterceptor already shows its own error snackbar for the failed request either way.
 */
@Injectable()
export class UserManagementSharedDataStore extends ComponentStore<IUserManagementSharedDataState> {
  public readonly roles$ = this.selectSignal((state: IUserManagementSharedDataState) => state.roles);
  public readonly teams$ = this.selectSignal((state: IUserManagementSharedDataState) => state.teams);
  public readonly users$ = this.selectSignal((state: IUserManagementSharedDataState) => state.users);
  public readonly userInvitations$ = this.selectSignal(
    (state: IUserManagementSharedDataState) => state.userInvitations,
  );

  private rolesInFlight = false;
  private teamsInFlight = false;
  private usersInFlight = false;
  private userInvitationsInFlight = false;

  // Populated the instant a row's optimistic delete fires (see removeRole/removeTeam/removeUser's
  // callers) and cleared once that row's own delete request settles either way. Exists so a
  // reload triggered by a *different* row's delete failure can't resurrect a row whose own delete
  // is still in flight: the reload's fresh GET response is filtered against these sets below
  // (fetchRoles/fetchTeams/fetchUsers) before being written to state, since the server may not
  // have committed this row's still-pending delete yet.
  private readonly pendingDeleteRoleIds = new Set<string>();
  private readonly pendingDeleteTeamIds = new Set<string>();
  private readonly pendingDeleteUserIds = new Set<string>();

  private readonly fetchRoles = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      switchMap(() =>
        this.roleService.getRoles(new HttpParams().set('limit', FETCH_LIMIT)).pipe(
          tap((response: IApiResponse<IRole[]>) => {
            this.patchState({
              roles: (response.data ?? []).filter((role) => !this.pendingDeleteRoleIds.has(role.id)),
            });
            this.rolesInFlight = false;
          }),
          // Deliberately no patchState here on failure, whether this is a reload or a genuine
          // initial load - see the class doc comment's "fetch failure handling" paragraph.
          catchError(() => {
            this.rolesInFlight = false;

            return EMPTY;
          }),
        ),
      ),
    );
  });

  private readonly fetchTeams = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      switchMap(() =>
        this.teamService.getTeams(new HttpParams().set('limit', FETCH_LIMIT)).pipe(
          tap((response: IApiResponse<ITeam[]>) => {
            this.patchState({
              teams: (response.data ?? []).filter((team) => !this.pendingDeleteTeamIds.has(team.id)),
            });
            this.teamsInFlight = false;
          }),
          // Deliberately no patchState here on failure, whether this is a reload or a genuine
          // initial load - see the class doc comment's "fetch failure handling" paragraph.
          catchError(() => {
            this.teamsInFlight = false;

            return EMPTY;
          }),
        ),
      ),
    );
  });

  private readonly fetchUsers = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      switchMap(() =>
        // `joins=all` - without it, GET /users noloads the `role`/`valid_user_groups`
        // relationships server-side, so every user's `roles`/`teamId` comes back empty/null
        // unconditionally, silently zeroing out role/team membership everywhere this is used.
        this.userService.getUsers(new HttpParams().set('limit', FETCH_LIMIT).set('joins', 'all')).pipe(
          tap((response: IApiResponse<IUser[]>) => {
            this.patchState({
              users: (response.data ?? []).filter((user) => !this.pendingDeleteUserIds.has(user.id)),
            });
            this.usersInFlight = false;
          }),
          // Deliberately no patchState here on failure, whether this is a reload or a genuine
          // initial load - see the class doc comment's "fetch failure handling" paragraph.
          catchError(() => {
            this.usersInFlight = false;

            return EMPTY;
          }),
        ),
      ),
    );
  });

  /** Deliberately independent from UserManagementUsersTabStore.fetchInvitations, rather than that
   * store handing its own copy up to this one - that store's copy is filtered/shaped for the
   * Users tab's own table (and is only guaranteed to exist while that tab happens to be mounted),
   * whereas this one needs to exist regardless of which tab the user is currently on, and needs
   * every invitation (accepted included, so upsert/removeUserInvitation below can be called
   * unconditionally without the caller having to know this invitation's status). Kept in sync with
   * every place that changes an invitation's identity or its role/team target - creating one
   * (UserManagementUsersTabComponent.openInviteDialog), resending one (resendInvitation, which
   * swaps the invitation's id), revoking one (revokeInvitation), editing a still-pending one's role
   * or team (patchPendingInvitation), and a joined user's invitation being cleared out on delete
   * (deleteJoinedUser) - via upsertUserInvitation/removeUserInvitation, same as roles/teams/users'
   * own upsert/remove mutators. Same fetch-failure handling as fetchRoles/fetchTeams/fetchUsers -
   * see the class doc comment's "fetch failure handling" paragraph. */
  private readonly fetchUserInvitations = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      switchMap(() =>
        this.userInvitationService.getCampaignSubUserInvitations(new HttpParams().set('limit', FETCH_LIMIT)).pipe(
          tap((response: IApiResponse<IUserInvitation[]>) => {
            this.patchState({ userInvitations: response.data ?? [] });
            this.userInvitationsInFlight = false;
          }),
          catchError(() => {
            this.userInvitationsInFlight = false;

            return EMPTY;
          }),
        ),
      ),
    );
  });

  constructor(
    private readonly roleService: RoleService,
    private readonly teamService: TeamService,
    private readonly userService: UserService,
    private readonly userInvitationService: UserInvitationService,
  ) {
    super(initialState);
  }

  /** Fetches whichever of roles/teams/users/userInvitations haven't been loaded yet (and aren't
   * already being fetched) - called once from `UserManagementComponent`'s constructor, i.e. once
   * per page visit, since this store itself is recreated fresh each time that component is. */
  public ensureLoaded(): void {
    this.ensureRolesLoaded();
    this.ensureTeamsLoaded();
    this.ensureUsersLoaded();
    this.ensureUserInvitationsLoaded();
  }

  public ensureUserInvitationsLoaded(): void {
    if (this.userInvitations$() !== null || this.userInvitationsInFlight) {
      return;
    }

    this.userInvitationsInFlight = true;
    this.fetchUserInvitations();
  }

  /** See reloadRoles' doc comment - identical purpose, for invitations. Used by
   * UserManagementUsersTabComponent.deleteJoinedUser's failure path alongside its own
   * `fetchInvitations()`, since that failure means this store's own optimistic
   * upsert/removeUserInvitation calls may now be out of step with the server too. */
  public reloadUserInvitations(): void {
    this.userInvitationsInFlight = true;
    this.fetchUserInvitations();
  }

  public upsertUserInvitation(userInvitation: IUserInvitation): void {
    const userInvitations = this.userInvitations$() ?? [];
    const exists = userInvitations.some((existing) => existing.id === userInvitation.id);

    this.patchState({
      userInvitations: exists
        ? userInvitations.map((existing) => (existing.id === userInvitation.id ? userInvitation : existing))
        : [...userInvitations, userInvitation],
    });
  }

  public removeUserInvitation(id: string): void {
    this.patchState({
      userInvitations: (this.userInvitations$() ?? []).filter((userInvitation) => userInvitation.id !== id),
    });
  }

  public ensureRolesLoaded(): void {
    if (this.roles$() !== null || this.rolesInFlight) {
      return;
    }

    this.rolesInFlight = true;
    this.fetchRoles();
  }

  public ensureTeamsLoaded(): void {
    if (this.teams$() !== null || this.teamsInFlight) {
      return;
    }

    this.teamsInFlight = true;
    this.fetchTeams();
  }

  public ensureUsersLoaded(): void {
    if (this.users$() !== null || this.usersInFlight) {
      return;
    }

    this.usersInFlight = true;
    this.fetchUsers();
  }

  /** Forces a full refetch of roles/teams/users, bypassing the "already loaded" null-check that
   * ensure*Loaded() uses. Used to recover this store's state after an optimistic row removal
   * (removeRole/removeTeam/removeUser) whose underlying delete request then turned out to have
   * failed - there's no per-row data left to roll back to at that point (the row is already gone
   * from every consumer), so a full reload from the server is the only correct fix. */
  public reloadRoles(): void {
    this.rolesInFlight = true;
    this.fetchRoles();
  }

  public reloadTeams(): void {
    this.teamsInFlight = true;
    this.fetchTeams();
  }

  public reloadUsers(): void {
    this.usersInFlight = true;
    this.fetchUsers();
  }

  public setRoles(roles: IRole[]): void {
    this.patchState({ roles });
  }

  public setTeams(teams: ITeam[]): void {
    this.patchState({ teams });
  }

  public upsertRole(role: IRole): void {
    const roles = this.roles$() ?? [];
    const exists = roles.some((existingRole) => existingRole.id === role.id);

    this.patchState({
      roles: exists
        ? roles.map((existingRole) => (existingRole.id === role.id ? role : existingRole))
        : [...roles, role],
    });
  }

  /** Marks `id` as having an in-flight delete request - called immediately alongside
   * `removeRole`'s optimistic removal (see UserManagementRolesTeamsTabComponent.onDeleteRoleClick).
   * Cleared via `clearRoleDeletePending` once that request settles either way. See the
   * `pendingDeleteRoleIds` field doc comment for why this exists. */
  public markRoleDeletePending(id: string): void {
    this.pendingDeleteRoleIds.add(id);
  }

  public clearRoleDeletePending(id: string): void {
    this.pendingDeleteRoleIds.delete(id);
  }

  public removeRole(id: string): void {
    this.patchState({ roles: (this.roles$() ?? []).filter((role) => role.id !== id) });
  }

  public upsertTeam(team: ITeam): void {
    const teams = this.teams$() ?? [];
    const exists = teams.some((existingTeam) => existingTeam.id === team.id);

    this.patchState({
      teams: exists
        ? teams.map((existingTeam) => (existingTeam.id === team.id ? team : existingTeam))
        : [...teams, team],
    });
  }

  /** See markRoleDeletePending's doc comment - identical purpose, for teams. */
  public markTeamDeletePending(id: string): void {
    this.pendingDeleteTeamIds.add(id);
  }

  public clearTeamDeletePending(id: string): void {
    this.pendingDeleteTeamIds.delete(id);
  }

  public removeTeam(id: string): void {
    this.patchState({ teams: (this.teams$() ?? []).filter((team) => team.id !== id) });
  }

  public upsertUser(user: IUser): void {
    const users = this.users$() ?? [];
    const exists = users.some((existingUser) => existingUser.id === user.id);

    this.patchState({
      users: exists
        ? users.map((existingUser) => (existingUser.id === user.id ? user : existingUser))
        : [...users, user],
    });
  }

  /** See markRoleDeletePending's doc comment - identical purpose, for users. */
  public markUserDeletePending(id: string): void {
    this.pendingDeleteUserIds.add(id);
  }

  public clearUserDeletePending(id: string): void {
    this.pendingDeleteUserIds.delete(id);
  }

  public removeUser(id: string): void {
    this.patchState({ users: (this.users$() ?? []).filter((user) => user.id !== id) });
  }
}
